import * as R from 'remeda'
import invariant from 'tiny-invariant'
import { v4 as uuid } from 'uuid'

export const LS_COOKIES_ACCEPTED = 'cookies_accepted' as const
export const LS_COOKIES_DECLINED = 'cookies_declined' as const

/**
 * Lifetime of the GTO_AFFILIATE_ID cookie, in days — the affiliate T&C §8
 * last-click window of 6 months, which spans 181-184 days depending on the
 * start month. 183 is the value approved on MKT-886; it covers every span but
 * the 184-day ones (Mar/May/Jul/Aug starts), where the last day is a knowingly
 * accepted gap. That gap is the open product call tracked on MKT-1312 — note
 * that closing it means moving the backend's CLICK_FRESHNESS_WINDOW too, since
 * a click past that window is refused server-side whatever the cookie says.
 * A shorter cookie silently drops clicks the contract still promises to
 * honour, so never trim this below 181.
 *
 * `CLICK_FRESHNESS_WINDOW` is 183 days, in
 * `app/app/account/affiliates/services/attribution_rules.py`, which declares itself the
 * single source of truth for every attribution window and requires bumping
 * `AffiliateAttributionRuleVersion` when one moves. Do NOT read it out of the
 * `repos/gto-wizard` clone without fetching first: that clone runs hundreds of commits
 * behind, its default branch is `master` rather than `main`, and an older revision of
 * this constant said 48 hours — a review round already mis-graded this comment on
 * exactly that stale reading. Two OTHER windows in that file are deliberately not this
 * one: `DORMANCY_WINDOW` (183 days, payment dormancy) and `LIFETIME_WINDOW`
 * (24 months, the held-customer lifetime).
 *
 * SINCE MKT-886 THIS VALUE ALSO DECIDES WHETHER A CLICK IS RE-DATED, which is a stronger
 * coupling than it used to carry. It once only sized a cookie, so drift against
 * `CLICK_FRESHNESS_WINDOW` was self-correcting in the safe direction — the server refuses
 * a stale click whatever the cookie says. `ensureAffiliateClickStamped` now treats a date
 * past this many days as evidence of a NEW click, so a value BELOW the backend window
 * re-stamps clicks the backend still considers live and silently over-attributes. Lower
 * this only together with `CLICK_FRESHNESS_WINDOW`, and never as a unilateral frontend
 * change.
 */
export const AFFILIATE_COOKIE_DAYS = 183

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * What an affiliate code may contain.
 *
 * `recordAffiliateClick`'s only caller feeds it a raw URL path segment
 * (`/p/:affiliateId`), so this is the boundary where untrusted input stops.
 *
 * DELIBERATELY AS WIDE AS THE BACKEND, because the backend's own rule is length. Only
 * self-serve codes come from a restricted alphabet (`[A-Z0-9]`). Admin CREATION
 * constrains the prefix it generates, but the admin UPDATE schema takes a bare
 * `str | None` with no charset validator, and legacy-migrated codes are copied verbatim
 * from a `max_length=128` column. A narrower FE pattern would drop a real partner's
 * hand-made code and cost them 100% of their attribution.
 *
 * The three excluded characters are not a policy choice, they are what a cookie cannot
 * physically carry: `;` starts an attribute and CR/LF end the header (RFC 6265 §4.1.1),
 * so a code containing one could never round-trip through `GTO_AFFILIATE_ID` no matter
 * what any validator allowed. Excluding them HERE rather than relying on the
 * `setCrossDomainCookie` invariant is deliberate: the invariant throws, and this runs
 * inside a vue-router `beforeEnter`, where a throw costs the visitor their navigation.
 * Rejecting returns `'invalid-code'` and the caller logs and routes on. Keep the two in
 * step — if that invariant ever guards another character, it belongs here too.
 *
 * Surrounding whitespace is not a residual to tolerate. `getCookie` trims the whole
 * cookie pair BEFORE slicing the name off, so trailing padding could never round-trip
 * byte-exact; leading padding survives that but not the read-back check.
 * `recordAffiliateClick` trims first, so both ends are handled before validation.
 *
 * The LENGTH is the contract half: 100 matches `linkTouchBodyCodeMax` in the generated
 * client AND `AffiliateClick.code`, the column `/link-touch/` actually writes into. The
 * other two are different columns for different things — `AffiliateCoupon.code` is 64,
 * `AffiliateCode.code` is 128 — which leaves a real residual at the top end: a legacy
 * code of 101-128 characters fits the column it lives in but can neither pass this
 * endpoint nor be stored as a click, so widening here alone would not help it.
 *
 * Not imported: helpers is a leaf lib with no `@gto/api-client` dependency (see
 * `dev-tools-listener.ts`). `libs/api-client` CAN see both, so the two are tied together
 * by an assertion test there rather than by this sentence.
 */
export const AFFILIATE_CODE_PATTERN = /^[^;\r\n]{1,100}$/

/**
 * sessionStorage key marking the affiliate code already reported to
 * `POST /v4/affiliate-coupons/link-touch/`. Shared by gto-wizard-web and
 * gto-wizard-web-vue so a session that crosses both apps reports once.
 */
export const AFFILIATE_LINK_TOUCH_REPORTED_KEY = 'gw-affiliate-link-touch-reported' as const

export const essentialCookies = [
  'GTO_LANGUAGE', // ensures the server (and all subdomains) immediately know which language to render, SSR & SEO benefits
  'GTO_G1', // flag which disables some links for better SEO (so that google doesn't think we're a gambling site)
  'GTO_APP_UID', // added to all requests (GWCLIENTID header) to identify the user, helps prevent multi-accounting
  'GTO_REFERRER', // used by affiliate links to track the referral source of the user
  'GTO_AFFILIATE_ID', // used to track the affiliate ID of the user
  'GTO_AFFILIATE_CLICKED_AT', // when GTO_AFFILIATE_ID was set; essential for the same reason it is
  'GTO_LOGGED_IN', // used to track if the user is logged in
  'GTO_COOKIES_CONSENT', // used to track if the user has consented to cookies
] as const

export type CookieName =
  | 'GTO_GCLID'
  | 'GTO_trackAffil'
  | 'GTO_TRITON_BOARD'
  | 'GTO_USERID'
  // The affiliate flow's completion marker carries the partner's code, so it cannot be a
  // literal. Named here rather than left to a cast at each call: `isAffiliateFlowMarker`
  // below exempts exactly this shape, and without the member no value of this type could
  // ever satisfy that predicate — the type would say the exemption is unreachable while
  // `affiliate.js` reaches it on every affiliate visit.
  | `GTO_${string}Finished`
  | (typeof essentialCookies)[number]

export const CookieConsent = Object.freeze({
  Accepted: 'accepted',
  Declined: 'declined',
} as const)

/** What the visitor answered. `null` alongside it always means they have not answered yet. */
export type ConsentDecision = 'accepted' | 'declined'

/**
 * What the visitor answered, as the consent cookie records it.
 *
 * Only an exact `accepted` is an Allow. Any OTHER value is a Reject, not just `declined`: the
 * cookie is shared by every `.gtowizard.com` host, and the Webflow homepage's banner writes
 * `denied` for its Reject. Matching `declined` alone read that Reject as no answer at all. An
 * absent or empty cookie means the visitor has not answered.
 *
 * This is the ONE place a caller that can import it reads that rule. `readStoredConsent` in
 * `@gto/analytics/consent` adds the legacy localStorage fallback on top of this call, and the
 * inlined bootstrap in `consent-bootstrap.mjs` restates the rule as source text — it has to,
 * because a Vite config evaluates it in Node before any browser toolchain exists, and a test
 * pins that copy to these same constants. A second IMPORTABLE copy would be one too many: the
 * two readers decide, respectively, what this app may store and what every tag on the page may
 * do, and they must never disagree about one cookie.
 */
export const readConsentCookie = (): ConsentDecision | null => {
  const consent = getCookie('GTO_COOKIES_CONSENT')

  if (consent === CookieConsent.Accepted) {
    return 'accepted'
  }

  return consent === '' ? null : 'declined'
}

/**
 * The affiliate flow's completion marker, `GTO_<partner>Finished`.
 *
 * It carries the partner's code in its own NAME, so the list above cannot hold it, and
 * `affiliate.js` builds it at runtime in an untyped file where `CookieName` constrains nothing.
 *
 * Essential because it ENDS the flow, not because it tracks anything. `trackAffiliate()` reads
 * it to decide whether this partner was already credited. Blocking the write does not stop the
 * credit — the Webflow partner page writes the two cookies that START the flow with a raw
 * `document.cookie`, and asks nothing about consent — it only stops the flow ever finishing. The
 * marker never lands, the check at the top never trips, and every later app load pushes another
 * `partner-data/<user>` record to Firebase. That duplication is silent, unbounded, and it
 * inflates the record the affiliate is paid against.
 *
 * Whether the flow should run AT ALL for a visitor who rejected tracking is a product question
 * for marketing. Gating it here would answer that question by dropping the partner's
 * attribution; this predicate only keeps the flow running the number of times it was designed
 * to run.
 */
const isAffiliateFlowMarker = (cname: CookieName): boolean => /^GTO_.+Finished$/.test(cname)

/**
 * Whether the consent gate must write this cookie whatever the visitor answered.
 *
 * ONE answer site, deliberately. The gate reads the very cookie it gates, so
 * `GTO_COOKIES_CONSENT` sitting on the essential list is the only thing that lets a visitor who
 * rejected ever accept again. Spreading "essential" across a list and a separate predicate puts
 * that escape one refactor away from disappearing, and the affiliate cookies next to it.
 */
const isEssential = (cname: CookieName): boolean =>
  R.isIncludedIn(cname, essentialCookies) || isAffiliateFlowMarker(cname)

/** Whether a non-essential cookie must not be written, because the visitor rejected tracking. */
export const isCookieDisabled = (cname: CookieName): boolean =>
  readConsentCookie() === 'declined' && !isEssential(cname)

export const getCookie = (cname: CookieName): string => {
  const name = `${cname}=`
  const ca = document.cookie.split(';')
  for (const c of ca) {
    const cookie = c.trim() || ''
    if (cookie.startsWith(name)) {
      return cookie.substring(name.length, cookie.length)
    }
  }

  return ''
}

export const isG1Enabled = (): boolean => getCookie('GTO_G1') === 'enabled'

/**
 * Writes a cookie for `.gtowizard.com`.
 *
 * `content` is interpolated into the cookie string ahead of `expires`/`path`/`Secure`,
 * so a `;` in it would not be stored as data — it would start a new cookie ATTRIBUTE,
 * and per RFC 6265 §5.3 a `Max-Age` smuggled in that way overrides the `expires` set
 * below. That is the whole attack: a value carrying `;Max-Age=630720000` outlives the
 * lifetime this function was asked for. `;` CR and LF cannot appear in a legitimate
 * cookie value (RFC 6265 §4.1.1), so this is a caller bug rather than a runtime
 * condition — it throws instead of quietly mangling the value, and reaches Sentry via
 * the normal error path. The check survives a production build; the lazily-built message
 * does not, so what actually arrives there is `Invariant failed` plus a stack. Validate untrusted input BEFORE calling (see
 * `recordAffiliateClick`); do not rely on this backstop to make a decision.
 *
 * `name` is checked as well as `content`, because it lands in the same string and is not
 * always a literal — `affiliate.js` builds `GTO_${trackAffil}Finished` in an untyped file,
 * where `CookieName` constrains nothing. That caller cannot actually fire it: `trackAffil`
 * comes from `getCookie`, whose output structurally cannot contain these characters. This
 * is depth against a FUTURE runtime-built name, which is also why the test has to defeat
 * the type to reach it.
 *
 * Both checks run BEFORE the cookies-disabled early return, deliberately — a caller
 * passing a delimiter is a bug whether or not this particular visitor would have been
 * written to, and a bug that only surfaces for consenting users is worse.
 */
export const setCrossDomainCookie = (name: CookieName, content: string, exdays = 30): void => {
  invariant(
    !/[;\r\n]/.test(name),
    () =>
      `refusing to write a cookie: name ${JSON.stringify(name)} contains a cookie-attribute delimiter`,
  )
  invariant(
    !/[;\r\n]/.test(content),
    () => `refusing to write cookie ${name}: value contains a cookie-attribute delimiter`,
  )
  // An expiring write is a DELETION, and a deletion is never gated. The consent gate stops a
  // non-essential cookie being STORED; applying it to a removal would keep one alive for exactly
  // the visitor who asked not to be tracked — `deleteCookie('GTO_GCLID')` on login would quietly
  // do nothing, while the ungated writer in `index.html` re-plants the click id for another 30
  // days on every visit that carries a `gclid` parameter. The bypass cannot store anything
  // either: the browser drops a cookie whose expiry has already passed.
  //
  // Asked as `<= 0` rather than `> 0` so that a value which is neither — `NaN` — stays GATED.
  // It would otherwise skip the gate and then produce `expires=Invalid Date`, an attribute the
  // browser ignores, storing the cookie for the session (RFC 6265 §5.2.1). No caller can reach
  // that today; the comparison simply must not be the thing that makes it reachable.
  const isDeletion = exdays <= 0
  if (!isDeletion && isCookieDisabled(name)) {
    return
  }
  const d = new Date()
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000)
  const expires = `expires=${d.toUTCString()}`
  const str = `${name}=${content};${expires};path=/;Secure;domain=.gtowizard.com`
  document.cookie = str
}

/**
 * Removes a cookie, whatever the visitor answered about consent.
 *
 * Spelled as a write with an expiry in the past, which is the only way a browser removes a
 * cookie. `setCrossDomainCookie` reads that zero as the deletion it is and skips the consent
 * gate for it — see the note there.
 */
export const deleteCookie = (name: CookieName): void => {
  setCrossDomainCookie(name, 'deleteme', 0)
}

export const generateAppUID = (): string => uuid()

/**
 * Records an affiliate link click — the code AND when it happened.
 *
 * The two are written here together, and read together by `getAffiliateClick`,
 * because attribution is a function of both. A code stored without its click time
 * gets reported to `/link-touch/` as a brand-new click on every browser session
 * (the report is deduped in sessionStorage, which a new session does not carry
 * over), and each report restarts the 183-day freshness window — stretching a
 * single click to nearly twice the window the affiliate T&C actually grants.
 *
 * Sending a stable timestamp also makes the report idempotent: the backend's click
 * history is unique on (user, code, seen_at), so re-reporting the same click
 * collapses to the row already on file instead of appending a fresher one.
 *
 * ONE of two writers of this pair in this repo, and the only one that knows the real
 * click instant — it runs on the `/p/:affiliateId` route, where the click just happened.
 * `ensureAffiliateClickStamped` is the other, and it writes the same pair from a WEAKER
 * position: it stamps first-app-sight for a code the Webflow apex script left dateless,
 * because that is the best date anyone still has. Prefer this function wherever the click
 * is actually observable; the other one exists for the traffic that never reaches here.
 *
 * Returns WHY, not just whether. A boolean acquired two meanings once the read-back check
 * arrived — "this slug is junk" and "the browser refused to store a perfectly good code" —
 * and a caller that logs them identically tells a cookie-blocking visitor their partner's
 * code is invalid. `invalid-code` means the slug carries `;` CR or LF and could not
 * survive a cookie at all (junk, or someone probing the boundary); `not-stored` means the
 * jar rejected or truncated the write. Both deserve reporting, and they are different
 * problems to go and look at.
 *
 * A return value rather than a throw because this runs inside a route guard, where the
 * visitor still belongs on Login whatever happened.
 */
/**
 * How much cookie life a click dated `clickedAt` still has, never more than the window.
 *
 * THE rule for the affiliate pair, in one place because both writers need it and a pair
 * whose two halves disagree is the bug `getAffiliateClick` exists to detect. Clamped at
 * the top so a future date cannot buy extra life. NOT floored here: a zero or negative
 * result is meaningful (the click's window has closed) and each caller refuses the write
 * rather than passing it to `setCrossDomainCookie`, where a negative expiry is a delete.
 */
const affiliateCookieDaysFrom = (clickedAt: Date): number =>
  Math.min(
    (clickedAt.getTime() + AFFILIATE_COOKIE_DAYS * DAY_MS - Date.now()) / DAY_MS,
    AFFILIATE_COOKIE_DAYS,
  )

export const recordAffiliateClick = (
  rawCode: string,
  clickedAt: Date = new Date(),
): AffiliateClickRecord => {
  // Trimmed because the backend strips this value at both ends — the `/link-touch/`
  // schema's `_strip_code` validator and `record_click`'s `code.strip().upper()` — so the
  // trimmed form is what would be stored anyway, and without this a padded slug fails the
  // read-back below and gets reported as junk. Close but NOT an identical character set:
  // JS `trim()` strips U+FEFF where Python does not, and Python strips \x1c-\x1f and
  // \x85 where JS does not. Nothing in that gap survives the read-back either way.
  const code = rawCode.trim()
  if (!AFFILIATE_CODE_PATTERN.test(code)) {
    return 'invalid-code'
  }
  const previous = getCookie('GTO_AFFILIATE_ID')
  // Measured from the CLICK, not from this write, and the two differ whenever the caller
  // passes a `clickedAt` it did not just take from its own clock. The rule is
  // `ensureAffiliateClickStamped`'s and is argued there: a pair that outlives the window
  // its own click earned is the over-attribution this change exists to stop, and two
  // writers computing the same expiry by different rules is that bug waiting for a caller.
  const exdays = affiliateCookieDaysFrom(clickedAt)
  if (exdays <= 0) {
    // Refused rather than written. A write with a negative `exdays` is a DELETION, and the
    // read-back below would then see the jar's previous value, take the mismatch branch,
    // and delete a still-valid cookie for a click that is merely too old to record.
    //
    // Its OWN outcome, not `not-stored`: the jar did nothing wrong here, and the caller
    // reports `not-stored` to Sentry as a browser refusal. A click past its window is an
    // expected, uninteresting result — nothing to go and look at.
    return 'window-closed'
  }
  setCrossDomainCookie('GTO_AFFILIATE_ID', code, exdays)

  // Confirm the browser actually stored what we validated, before writing the sidecar
  // that describes it. The pattern is a necessary condition, not a sufficient one: a
  // control character makes the write a silent no-op and a NUL truncates the value —
  // either of which would otherwise report success for a code that is not in the jar.
  // Checking here rather than widening the pattern keeps one rule (what a cookie can
  // hold) in one place: the jar itself.
  //
  // `GTO_AFFILIATE_ID` is in `essentialCookies`, so a declined consent cannot make this
  // fail — otherwise this would report every declining visitor as a bad code.
  const stored = getCookie('GTO_AFFILIATE_ID')
  if (stored !== code) {
    if (stored !== previous) {
      // A TRUNCATED write: the jar now holds a prefix, and whatever was here before is
      // gone. Delete rather than restore — re-writing `previous` would reset its 183-day
      // expiry and hand that click a fresh window, which is the over-attribution this
      // whole change exists to stop. Deleting only the code is deliberate: the sidecar
      // names its own code, so it stays correct and will pair again if that code is
      // legitimately re-written later.
      deleteCookie('GTO_AFFILIATE_ID')
    }

    return 'not-stored'
  }
  // The code is embedded, not just the time. Two cookies with independent lifetimes
  // cannot be kept in step by writing them together — see `getAffiliateClick`.
  setCrossDomainCookie('GTO_AFFILIATE_CLICKED_AT', `${code}|${clickedAt.toISOString()}`, exdays)

  return 'recorded'
}

/**
 * What `recordAffiliateClick` did — and when it did not record, which of the two reasons.
 * A union rather than a boolean so the caller can say which happened; see that function.
 */
export type AffiliateClickRecord = 'recorded' | 'invalid-code' | 'not-stored' | 'window-closed'

/** An affiliate link click as the browser knows it: the code, and when it happened. */
export type AffiliateClick = { code: string; clickedAt?: string }

/**
 * The affiliate click to report, or null when there is none.
 *
 * `clickedAt` is absent unless the sidecar PROVES it describes the code cookie sitting
 * next to it. Omitting it lets the server fall back to its own clock, so such a visitor
 * degrades to the old accuracy rather than being dropped.
 *
 * WHY THE SIDECAR CARRIES THE CODE. The two cookies have independent lifetimes and
 * independent writers, so writing them together does not keep them together. This
 * app writes both at 183 days; the Webflow apex script rewrites `GTO_AFFILIATE_ID`
 * alone at 60 days. A visitor who clicked PARTNERA here and PARTNERB there months
 * later would otherwise read back `{ code: PARTNERB, clickedAt: <PARTNERA's click> }`
 * — dating PARTNERB's click up to 183 days before it happened and shortening, or
 * zeroing, the window their commission depends on. Sending nothing is always safe;
 * sending a wrong time is not.
 *
 * WHAT THIS DOES NOT CATCH, and cannot: a SAME-code rewrite. If Webflow rewrites the ID
 * to the code the sidecar already names, the codes match and the old time is sent for a
 * genuinely newer click. The comparison has no way to see it — only the apex script
 * writing the paired sidecar does, which is MKT-1314. The cost is bounded rather than
 * silent-and-open: `record_click` is `get_or_create(user, code, seen_at)`, so the click
 * keeps its ORIGINAL time — nothing is overwritten and no history is lost. It therefore
 * ages out on the original schedule rather than the re-click's. (Usually by finding the
 * existing row; a visitor who was anonymous at the first click has no row yet, so one is
 * created at the old time instead. Same outcome, different path.) That trade is accepted until MKT-1314; it
 * is NOT something the pairing check below fixes, and must not be described as if it
 * were.
 *
 * A sidecar with no `|` is DROPPED rather than trusted: there is no way to tell a
 * still-valid one from a stale one, and guessing reinstates the bug above. This is not a
 * migration branch — `GTO_AFFILIATE_CLICKED_AT` has never existed on `main`, so no
 * release ever wrote the bare form and there is no legacy cohort to age out. It guards
 * against a hand-set or third-party-written value, which is why it does not expire.
 *
 * **An absent `clickedAt` is now a NARROW case, and was the common one.** Real partner
 * links (`gtowizard.com/p/<slug>`) 301 to the Webflow apex as `/?p=<slug>`, and an inline
 * script there writes `GTO_AFFILIATE_ID` itself — at a hardcoded 60 days, with no sidecar
 * — so `recordAffiliateClick` never runs for that traffic. Every report from it used to
 * omit `clicked_at`, the server dated each to now, and the window restarted on every
 * browser session: the exact bug MKT-886 set out to fix. What closed it is
 * `ensureAffiliateClickStamped`, which gives such a code a date the first time this app
 * sees it, so the omission now survives only until the visitor reaches the app — and
 * what reaches the server after that is a stable date.
 *
 * Three paths still reach a dateless report: a jar that refuses the stamp
 * (`not-stored`), an apex cookie that expired at day 60 before the visitor ever opened
 * the app, and a caller whose own `code` disagrees with the cookie — which
 * `affiliateAuthParams` drops on purpose, and which becomes reachable the moment the
 * "unify for all providers" TODO in `Login.vue` carries `affiliate_id` across an OAuth
 * round-trip. The second needs the apex script's 60 to become 183 (MKT-1314). None of
 * the three is "a legacy cookie that will age out": omitting `clickedAt` stays the
 * correct answer for them, and the server's own clock stays the fallback.
 */
/**
 * Whether a sidecar's timestamp is the shape `/link-touch/` will accept.
 *
 * `Date.parse` alone is too lax — it accepts `'2026'` and a range of locale strings the
 * endpoint's `datetime` type rejects — so the format is checked first and the value
 * second, which also rules out a well-shaped but impossible date like month 13.
 */
const isIsoTimestamp = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) &&
  !Number.isNaN(Date.parse(value))

export const getAffiliateClick = (): AffiliateClick | null => {
  const code = getCookie('GTO_AFFILIATE_ID')
  if (!code) {
    return null
  }
  // `getCookie` returns '' for an absent cookie, and ''.indexOf is -1 — so this one
  // check covers both "no sidecar" and "sidecar from before pairing existed".
  const sidecar = getCookie('GTO_AFFILIATE_CLICKED_AT')
  // LAST separator, not the first: a code may now contain `|`, and an ISO timestamp
  // never does — so everything before the final `|` is the code.
  const separator = sidecar.lastIndexOf('|')
  if (separator === -1) {
    return { code }
  }
  const clickedAt = sidecar.slice(0, separator) === code ? sidecar.slice(separator + 1) : ''

  // Shape-checked, not just non-empty. `/link-touch/` types `clicked_at` as a datetime,
  // so a corrupt value is a 422 — which is correctly reported and correctly leaves the
  // session unmarked, meaning every later session retries and 422s again for the cookie's
  // full 183 days. A visitor with a CORRUPT sidecar would report nothing at all, while a
  // visitor with NO sidecar reports fine. Both in-repo writers go through
  // `toISOString()` — `recordAffiliateClick` and `ensureAffiliateClickStamped` — so
  // neither can produce one; MKT-1314 adds a third from the Webflow apex, and the pairing
  // guard above already assumes a hand-set or third-party value is possible. Same rule
  // for the value as for the code: when in doubt, drop it and let the server use its own
  // clock.
  return isIsoTimestamp(clickedAt) ? { code, clickedAt } : { code }
}

/**
 * What `ensureAffiliateClickStamped` found, and what it did about it.
 *
 * Four facts, not a boolean, and only one of them is a problem: `not-stored` means this
 * visitor's jar refuses the write, so they are permanently on the server's clock. The
 * two callers that CAN report it do — see `affiliateAuthParams` for the one that cannot.
 */
export type AffiliateStamp = 'stamped' | 'extended' | 'no-code' | 'not-stored'

/**
 * Pins the affiliate cookie pair to its click's own freshness window: stamps a date the
 * first time this app sees a code without one, and keeps both cookies alive exactly as
 * long as that date is still worth reporting.
 *
 * THE RULE, and it is the whole function: **both cookies expire at
 * `clickedAt + AFFILIATE_COOKIE_DAYS`** — never at `now + AFFILIATE_COOKIE_DAYS`. The
 * stamp path and the re-extend path are the same code because that target is the same
 * expression; only where `clickedAt` comes from differs.
 *
 * WHY A DATE AT ALL. `GTO_AFFILIATE_ID` has two writers and only one writes the sidecar:
 * `recordAffiliateClick` does, on the SPA `/p/:affiliateId` route, and the Webflow apex
 * script does not. Real partner links 301 to the apex as `/?p=<slug>`, so in production
 * the cookie usually arrives DATELESS. A dateless code is reported to `/link-touch/`
 * with no `clicked_at`; the server dates it to its own clock, and because the dedupe
 * mark lives in sessionStorage the report repeats every browser session. Each one used
 * to restart the 183-day window, so the click never aged out and the partner kept
 * earning on a click from a year ago.
 *
 * WHY THE COOKIES ARE RE-WRITTEN WHEN THE DATE IS ALREADY THERE. Because the apex script
 * rewrites `GTO_AFFILIATE_ID` at 60 days whenever the visitor clicks a partner link, and
 * a rewrite resets the expiry DOWNWARD. Stamp on day 1 and the cookie runs to day 184;
 * re-click on day 50 and the apex cuts it to day 110 while the window this click earned
 * still runs to day 184 — so the code vanishes on day 111 and the last 74 days are the
 * same silent total loss the stamp exists to stop, worst for the visitor who uses the
 * partner's link most. An earlier version of this function set the lifetime once, at
 * stamp time, and had exactly that hole.
 *
 * Pinning to `clickedAt` is what makes re-writing safe to do on every load. The target
 * instant never moves, so calling this a thousand times is calling it once: the expiry
 * can only ever be the one the click already earned. The window is the backend's anyway
 * — it measures `CLICK_FRESHNESS_WINDOW` from `clicked_at` and refuses a stale click
 * whatever the cookie says — so the cookie only decides whether the code survives long
 * enough to be reported at all. Which is why it must not die EARLY either; that half is
 * what the re-extend fixes.
 *
 * A RECORDED DATE PAST ITS OWN WINDOW is re-stamped rather than kept, and that is the
 * one place this file has to agree with the backend. Every affiliate cookie this repo
 * writes is pinned to `clickedAt + AFFILIATE_COOKIE_DAYS` — here and in
 * `recordAffiliateClick`, both through `affiliateCookieDaysFrom`, which is why that
 * derivation is shared rather than written twice — and the apex script writes 60. So a
 * code still sitting in the jar beside a stale date cannot be the cookie that date was
 * reported from: its presence is evidence of a LATER write, which is a new click.
 * `record_click` draws the same inference from the same constant for an UNDATED report,
 * and the two must not diverge — if this side sent the stale date while that side would
 * have dated the report now, a genuine re-click would be dated by neither, because the
 * server clamps what we send and then refuses it as stale, and the partner earns nothing
 * on a sale they referred.
 *
 * The proof above is only as good as its premise, so the premise is ENFORCED rather than
 * argued: `affiliateCookieDaysFrom` clamps at the window, and a `recorded` ahead of `now`
 * is discarded instead of extended. Without the second of those a future date — a fast
 * device clock is enough — is kept at every load and re-written with an expiry that grows
 * without limit, which falsifies the premise using nothing but this function.
 *
 * In practice the re-stamp branch is rare, because pinning to `clickedAt` means our own
 * pair expires the moment the window does. What reaches it is a pair this repo did not
 * write: an apex cookie outliving a stale sidecar, or a hand-edited jar.
 *
 * The sidecar is written and read back BEFORE the code cookie is touched. Reversed, a jar
 * that took the ID write and dropped the sidecar would hand a still-dateless code 183
 * days of refreshing instead of 60 — strictly worse than doing nothing. In this order a
 * REJECTED sidecar write leaves the old expiry beside no date, which is where we started.
 * That is the ordering hazard in full: a TRUNCATING ID write would be the other one, and
 * it is unreachable rather than handled — truncation needs a NUL or a control character,
 * and `code` came out of `getCookie`, so it cannot carry one. (`recordAffiliateClick`
 * reads its ID write back because its code comes from a URL segment, which can.)
 *
 * A sidecar failing `getAffiliateClick`'s pairing or shape check counts as no date at all
 * and is overwritten. One naming a different code describes a code no longer in the jar;
 * a corrupt one would 422 on every report for the cookie's whole life.
 *
 * `code` is not re-validated against `AFFILIATE_CODE_PATTERN`, for the same reason
 * truncation is unreachable: out of the jar, it cannot carry the three characters that
 * pattern excludes. All the pattern would add is the endpoint's 100-character cap, and a
 * longer code is unreportable with or without a date.
 *
 * Does NOT catch. Blocked site data makes `document.cookie` throw and the callers own
 * that: the shared reporter is inside the try that already has the right Sentry message,
 * the Vue bootstrap wraps it so a cookie jar cannot take the app down at boot, and
 * `affiliateAuthParams` is reached only after its own caller read the cookie
 * successfully. Swallowing here would hide a failure already reported one level up.
 *
 * WHAT THIS STILL GETS WRONG — both directions, because a stamp is not a click:
 *
 * - First app sight is LATER than the click by however long the visitor spent on the
 *   marketing site, so the window is stretched by up to the apex cookie's 60 days. This
 *   is NOT a migration cohort that drains: click a link, read the marketing page, open
 *   the app a fortnight later, and it happens again. It favours the partner and it is
 *   capped at 60 days. Unfixable from here — nobody recorded the real instant — and what
 *   it replaced was unbounded.
 * - A RE-CLICK of the same code is invisible, so the window keeps the FIRST click's date
 *   and closes earlier than last-click-wins would grant. That favours the house. Only the
 *   apex writing the paired sidecar can see it.
 *
 * Both need the apex script to write `GTO_AFFILIATE_CLICKED_AT` and to use 183 days
 * rather than 60 — MKT-1314, not a change in this repo. Same for the visitor who clicks,
 * never opens the app, and returns after day 60: no cookie is left to find. Do not read
 * the residual as one-directional; it is not.
 */
export const ensureAffiliateClickStamped = (): AffiliateStamp => {
  const code = getCookie('GTO_AFFILIATE_ID')
  if (!code) {
    return 'no-code'
  }
  // Asked of the reader rather than re-derived, so "does this code have a date we can
  // vouch for" has exactly one definition. Everything the reader drops — an unpaired
  // sidecar, one naming another code, a corrupt timestamp — arrives here as no date.
  const recorded = getAffiliateClick()?.clickedAt
  // No `seenAt` parameter, deliberately. First sight is whenever this runs, and
  // `setCrossDomainCookie` measures `exdays` from its own `new Date()` — so a parameter
  // here would be a SECOND clock that the expiry silently ignores, in a function whose
  // whole job is to pin an expiry to an instant. Tests move the one clock with fake
  // timers instead. (`recordAffiliateClick` does take a date, and needs to: its click
  // instant comes from the route guard and is not necessarily its write time.)
  const now = Date.now()
  const recordedAt = recorded === undefined ? undefined : Date.parse(recorded)
  // A recorded date is kept only while it is LIVE: inside its window, and not ahead of
  // now. Both bounds matter and neither is theoretical.
  //
  // Past its window, re-stamped for the reason argued above — the code beside it must
  // have been written later, so this is a new click.
  //
  // AHEAD of now, discarded, because `isIsoTimestamp` proves shape and nothing else. A
  // future date is the one input that would otherwise grow without limit: it passes the
  // window test at every load, so it is kept forever and re-written each time with an
  // expiry however far ahead it sits PLUS the window. A device whose clock is fast writes
  // one at stamp time, and the absolute expiry outlives the clock being corrected — no
  // attacker needed. Dropping it puts that visitor on the same footing as a dateless one,
  // which is the honest reading: we have no date we can vouch for.
  const live =
    recordedAt !== undefined &&
    recordedAt <= now &&
    recordedAt + AFFILIATE_COOKIE_DAYS * DAY_MS > now
  const clickedAt = live ? new Date(recordedAt) : new Date(now)
  const exdays = affiliateCookieDaysFrom(clickedAt)
  const sidecar = `${code}|${clickedAt.toISOString()}`
  setCrossDomainCookie('GTO_AFFILIATE_CLICKED_AT', sidecar, exdays)
  if (getCookie('GTO_AFFILIATE_CLICKED_AT') !== sidecar) {
    return 'not-stored'
  }
  setCrossDomainCookie('GTO_AFFILIATE_ID', code, exdays)

  return live ? 'extended' : 'stamped'
}

/**
 * The sessionStorage value marking a click as already reported to `/link-touch/`.
 *
 * Keyed on the click TIME as well as the code, and derived here so the two apps
 * cannot disagree about what "already reported" means. Re-clicking the same
 * partner's link updates the cookie's timestamp, and that is a newer click the
 * server needs to hear about — keying on the code alone would swallow it for the
 * rest of the tab session, which only became possible once the timestamp stopped
 * being "now".
 */
export const affiliateClickReportKey = (click: AffiliateClick): string =>
  `${click.code}|${click.clickedAt ?? ''}`

/**
 * The affiliate fields for a login/register payload — `affiliate_id`, plus the click
 * time when we can vouch for it.
 *
 * CALLS A WRITER, and that is a conceded trade rather than an oversight.
 * `ensureAffiliateClickStamped` writes cookies, and this function's name promises a pure
 * params builder — exactly the invisible side effect that function's own rationale
 * rejects for `getAffiliateClick`. It is here anyway because a payload is the LAST moment
 * a click can be dated: after it the server has dated the click itself and nothing the
 * browser learns later moves that row. On the Vue host this runs before the app has ever
 * called the link-touch reporter, so leaving it to the callers would mean six call sites
 * across two apps each remembering to stamp first, and the one that forgot would lose a
 * partner their commission silently. One named call inside the builder is the smaller
 * trap. The cost is real and is not paid down: these six sites cannot report
 * `not-stored`, because `@gto/helpers` is a leaf lib with no route to `logSentry` (see
 * `affiliate-link-touch.ts` for the cycle). The two callers that can, do.
 *
 * The code/cookie match is DEFENSIVE — it does not describe a bug reachable today.
 * Every current caller passes a code that traces to a live `getCookie('GTO_AFFILIATE_ID')`
 * read in the same synchronous block as the `getAffiliateClick()` re-read below:
 * inline in `use-login-social-handlers`'s social payload, in `use-oauth-login`'s
 * Google/Facebook callback payload, in the standalone `routes/login/route.tsx` loader,
 * and in `Login.vue`'s `handleAppleAuth`, plus one hop
 * back through `params` at the `handleRedirectFromService` call sites, whose
 * `affiliate_id` the `created` hook assigns from its own cookie read in the same
 * synchronous block. Nothing persists `affiliate_id` across a page load — only
 * `socialCode` is stored — so the two reads cannot disagree.
 *
 * The inventory is now EXHAUSTIVE: the standalone-mode loader in `gto-wizard-web`'s
 * `routes/login/route.tsx` was the one producer of this payload that sent no affiliate
 * fields at all, and it routes through here too. Every login and register payload in
 * both apps is a caller, so a new one is the only way this list can go stale.
 *
 * Names symbols, not line numbers — a few lines moving in `Login.vue` would falsify
 * every number here at once.
 *
 * Keep the guard regardless: a caller that carried `affiliate_id` through storage across
 * an OAuth round-trip — the shape the "unify for all providers" TODO above
 * `handleAppleAuth` invites — would make the branch live. When they disagree we send
 * nothing and the server falls back to its own clock.
 *
 * It is NOT what stops a mispaired timestamp, though, and must not be credited with it.
 * Because every caller derives `code` from the same cookie `getAffiliateClick` re-reads,
 * this comparison is `X === X` for the one mispairing that IS reachable — a sidecar left
 * behind by an earlier click under a different code. That is caught inside
 * `getAffiliateClick`, by the code embedded in the sidecar itself.
 */
export const affiliateAuthParams = (
  code: string | undefined | null,
): { affiliate_id?: string; affiliate_clicked_at?: string } => {
  if (code === null || code === undefined || code === '') {
    return {}
  }
  // Guarded, because not every caller passes the jar's own code: `Login.vue:567` takes
  // `affiliate_id` back out of the OAuth redirect response. A jar holding someone else's
  // code must not be handed a fresh 183-day window by a payload that is about to refuse
  // to attribute it — see the match below, which drops the date in exactly that case.
  //
  // Asked of `getCookie`, not of the click record. Presence of the CODE is the whole
  // question here — a dateless cookie is precisely what we are here to stamp — and
  // `getAffiliateClick` answers a larger one, returning a record with no `clickedAt` for
  // that case. Reading the jar directly keeps this guard from depending on which of those
  // two shapes that function happens to return for a code with no date.
  if (getCookie('GTO_AFFILIATE_ID') === code) {
    // Writes cookies. Deliberate, and argued in the docstring above rather than here.
    ensureAffiliateClickStamped()
  }
  const click = getAffiliateClick()
  const clickedAt = click?.code === code ? click.clickedAt : undefined

  return clickedAt === undefined
    ? { affiliate_id: code }
    : { affiliate_id: code, affiliate_clicked_at: clickedAt }
}
